/**
 * Electric SQL → TanStack DB sync provider.
 *
 * Bridges Electric's durable shape streams into TanStack DB's sync protocol:
 *   begin() → write() per change message → commit()
 *
 * Key features:
 *  - Full lifecycle: initial snapshot replayed on first connect, then live updates
 *  - Resumability: persists the last Electric `offset` and `shapeHandle` in
 *    IndexedDB so a page reload can resume the stream instead of fetching a
 *    full snapshot.
 *  - AbortSignal support for clean collection teardown
 *  - SSR guard: returns a no-op when `fetch` is unavailable (server rendering)
 *  - Typed on the table row shape via generic parameter `T`
 *
 * Usage:
 * ```ts
 * import { createElectricSyncConfig } from '@/lib/db/electric-sync-provider'
 * import { createCollection } from '@tanstack/db'
 *
 * const todosCollection = createCollection<TodoRow>({
 *   id: 'todos',
 *   getKey: (row) => row.id,
 *   sync: createElectricSyncConfig<TodoRow>({
 *     url: `${ELECTRIC_URL}/v1/shape/todos`,
 *     scope: { user: 'user-123' },
 *   }),
 * })
 * ```
 */

import type { SyncConfig } from "@tanstack/db";
import { ShapeStream, isChangeMessage, type Offset } from "@electric-sql/client";
import { isElectricSyncEnabled } from "../electric";
import { loadShapeState, saveShapeState } from "./shape-state-store";
import { getAccessToken } from "./auth-store";

// ── Public types ─────────────────────────────────────────────────────────────

/**
 * Declared read scope for a shape. Serialized to `scope_*` query params —
 * the shape proxy verifies the caller's JWT and builds the actual SQL WHERE
 * server-side, so these values are assertions the proxy validates, never
 * trusted filters.
 */
export interface ShapeScope {
  /** Personal scope — must equal the authenticated user id. */
  user?: string;
  /** Organization scope — caller must be an org member. */
  org?: string;
  /** Row scope — caller must have access to this task (todos row). */
  task?: string;
  /** Row scope — caller must have access to this client (clients row). */
  client?: string;
  /** Session scope — caller must have access to this chat session. */
  session?: string;
}

const SHAPE_SCOPE_KEYS = ["user", "org", "task", "client", "session"] as const;

export interface ElectricSyncConfig {
  /**
   * Full shape URL including the table path segment.
   * Example: `https://electric.example.com/v1/shape/todos`
   */
  url: string;
  /**
   * Declared shape scope — forwarded to the proxy as `scope_*` params.
   * Omitting it produces an unfiltered request that the proxy rejects
   * (unless `allowUnfiltered` is set, which skips this client-side guard
   * but still requires the table's proxy rule to accept no scope).
   */
  scope?: ShapeScope;
  /**
   * Restrict columns synced to the client.
   */
  columns?: string[];
  /**
   * External AbortSignal that will stop the stream when triggered.
   */
  signal?: AbortSignal;
  /**
   * Explicitly allow an unscoped shape (no `scope_*` params). Default false:
   * the proxy only authorizes scoped shapes, so unscoped requests are
   * refused server-side anyway — this guard avoids starting doomed streams.
   */
  allowUnfiltered?: boolean;
}

// ── Internal helpers ──────────────────────────────────────────────────────────

/**
 * Merge two AbortSignals so that the resulting signal fires when either fires.
 * Falls back to a manual merge for environments without AbortSignal.any.
 */
function mergeSignals(a: AbortSignal, b: AbortSignal): AbortSignal {
  if (
    typeof AbortSignal !== "undefined" &&
    "any" in AbortSignal &&
    typeof (AbortSignal as { any?: unknown }).any === "function"
  ) {
    return (AbortSignal as { any: (signals: AbortSignal[]) => AbortSignal }).any(
      [a, b]
    );
  }
  const controller = new AbortController();
  const abort = () => controller.abort();
  a.addEventListener("abort", abort, { once: true });
  b.addEventListener("abort", abort, { once: true });
  return controller.signal;
}

function buildShapeUrl(config: ElectricSyncConfig): URL | null {
  try {
    const url = config.url.startsWith("http")
      ? new URL(config.url)
      : new URL(
          config.url,
          typeof window !== "undefined"
            ? window.location.origin
            : "http://localhost"
        );
    if (config.scope) {
      for (const key of SHAPE_SCOPE_KEYS) {
        const value = config.scope[key];
        if (value) url.searchParams.set(`scope_${key}`, value);
      }
    }
    if (config.columns?.length)
      url.searchParams.set("columns", config.columns.join(","));
    return url;
  } catch {
    return null;
  }
}

// ── Sync config factory ───────────────────────────────────────────────────────

/**
 * Create a TanStack DB `SyncConfig` object backed by an Electric durable stream.
 *
 * Pass the return value directly as the `sync` property of `createCollection(...)`.
 * The sync provider:
 *  1. Loads the last persisted offset/handle for this shape (if any).
 *  2. Creates a `ShapeStream` that subscribes to Electric from that position.
 *  3. On each batch of messages: writes change messages, then persists the new
 *     `offset` and `shapeHandle` so the next page load can resume.
 *  4. Returns a cleanup function that aborts the stream on collection teardown.
 *
 * TanStack DB's `write()` accepts:
 *  - Insert/Update: `{ type, value }` — key is derived via `getKey(value)`
 *  - Delete:        `{ type: 'delete', key }` — key passed explicitly
 */
export function createElectricSyncConfig<
  T extends object,
>(config: ElectricSyncConfig): SyncConfig<T> {
  return {
    sync({ begin, write, commit, markReady }) {
      // SSR guard — ShapeStream requires a fetch-capable runtime.
      if (typeof globalThis.fetch === "undefined") {
        markReady();
        return;
      }

      // Skip shape sync when Electric is not explicitly enabled (avoids MissingHeadersError spam).
      if (!isElectricSyncEnabled()) {
        markReady();
        return;
      }

      // No-op when Electric URL is invalid.
      if (!config.url || config.url === "/v1/shape") {
        markReady();
        return;
      }

      // Never start an unscoped shape: the proxy authorizes scope_* params,
      // so a missing scope would just be rejected server-side. The collection
      // stays empty (but still accepts local mutations + write-through).
      const hasScope =
        !!config.scope &&
        SHAPE_SCOPE_KEYS.some((key) => Boolean(config.scope?.[key]));
      if (!hasScope && !config.allowUnfiltered) {
        console.warn(
          `[electric-sync] refusing unscoped shape for ${config.url} — ` +
            `pass a scope or set allowUnfiltered`
        );
        markReady();
        return;
      }

      const url = buildShapeUrl(config);
      if (!url) {
        markReady();
        return;
      }

      // scope_* params are only honored by the authenticated shape proxy.
      // A direct Electric URL would silently ignore them and stream the
      // entire table — refuse to sync in that configuration.
      if (!url.pathname.startsWith("/api/electric/")) {
        console.warn(
          `[electric-sync] refusing scoped shape for non-proxy URL ${config.url} — ` +
            `point VITE_ELECTRIC_SHAPE_URL at /api/electric/shape`
        );
        markReady();
        return;
      }

      const shapeUrl = url.toString();
      const shapeKey = shapeUrl;
      const controller = new AbortController();
      const signal = config.signal
        ? mergeSignals(config.signal, controller.signal)
        : controller.signal;

      function startSync(resumeOffset?: string, resumeHandle?: string) {
        if (signal.aborted) {
          markReady();
          return;
        }

        // A non-trivial offset requires a shape handle to resume. Without one we
        // start from the beginning (or `now` if explicitly cached without handle).
        const startOffset: Offset = resumeHandle
          ? ((resumeOffset as Offset | undefined) ?? "-1")
          : resumeOffset === "now"
            ? "now"
            : "-1";

        const stream = new ShapeStream({
          url: shapeUrl,
          signal,
          offset: startOffset,
          handle: resumeHandle,
          // Dynamic header — re-read per request, so token rotation is
          // picked up automatically on reconnects/retries.
          headers: {
            Authorization: () => {
              const token = getAccessToken();
              return token ? `Bearer ${token}` : "";
            },
          },
          // Keep retrying on errors (bounded by the client's consecutive
          // retry guard): the header function re-reads the token each
          // attempt, so a 401 from a missing/expired token self-heals once
          // auth resolves.
          onError: () => ({}),
        });

        // Defer readiness until the first batch actually arrives. Marking ready
        // at subscribe time exposes an empty (but defined) collection, which
        // consumers read as "Electric is up" and skip the Supabase fallback.
        let ready = false;
        const readyOnce = () => {
          if (!ready) {
            ready = true;
            markReady();
          }
        };

        stream.subscribe((messages) => {
          const changes = messages.filter(isChangeMessage);
          if (changes.length > 0) {
            begin({ immediate: true });
            for (const msg of changes) {
              if (msg.headers.operation === "delete") {
                write({ type: "delete", key: msg.key });
              } else {
                write({
                  type: msg.headers.operation,
                  value: msg.value as T,
                });
              }
            }
            commit();
          }

          // Persist the stream's latest position so a reload can resume here.
          const handle = stream.shapeHandle;
          const offset = stream.lastOffset;
          if (handle) {
            void saveShapeState(shapeKey, { offset, handle });
          }

          readyOnce();
        });
      }

      loadShapeState(shapeKey)
        .then((state) => startSync(state?.offset, state?.handle))
        .catch(() => startSync());

      return () => {
        controller.abort();
      };
    },
  };
}
